import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { BlockMath } from "react-katex";
import { formatLatexForDisplay } from "../lib/latexUtils";

const statusOptions = ["all", "correct", "incomplete", "needs_review", "needs_improvement"];
const sortOptions = [
  { value: "recent", label: "Most Recent" },
  { value: "oldest", label: "Oldest" }
];

function formatAttemptDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function getStatusLabel(status) {
  if (status === "correct") return "Correct";
  if (status === "incomplete") return "Incomplete";
  if (status === "needs_improvement") return "Needs Improvement";
  return "Needs Review";
}

function getStatusClass(status) {
  if (status === "correct") return "status-correct";
  if (status === "incomplete") return "status-incomplete";
  if (status === "needs_improvement") return "status-incorrect";
  return "status-missing";
}

function getDifficultyBadge(diff) {
  const d = String(diff || "MOT").toUpperCase();
  const colors = {
    LOT: { bg: "#ecfdf5", border: "#a7f3d0", text: "#065f46" },
    MOT: { bg: "#fffbeb", border: "#fde68a", text: "#92400e" },
    HOT: { bg: "#fef2f2", border: "#fecaca", text: "#991b1b" }
  };
  const color = colors[d] || colors.MOT;
  return (
    <span
      style={{
        fontSize: "11px",
        fontWeight: "700",
        backgroundColor: color.bg,
        border: `1px solid ${color.border}`,
        color: color.text,
        padding: "2px 8px",
        borderRadius: "6px"
      }}
    >
      {d}
    </span>
  );
}

function CustomQuestionAttempts({ apiBase, token, onPageBusyChange }) {
  const { questionId } = useParams();
  const location = useLocation();
  const [question, setQuestion] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (typeof onPageBusyChange === "function") {
      onPageBusyChange("custom-question-attempts", loading, "Loading attempt history...");
    }
    return () => {
      if (typeof onPageBusyChange === "function") {
        onPageBusyChange("custom-question-attempts", false);
      }
    };
  }, [loading, onPageBusyChange]);

  useEffect(() => {
    let mounted = true;

    async function loadAttempts() {
      if (!token || !questionId) return;
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          sort: sortBy,
          limit: "50"
        });
        if (statusFilter !== "all") params.set("status", statusFilter);

        const response = await fetch(`${apiBase}/api/questions/custom/${questionId}/attempts?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.error || "Failed to load attempts");
        }

        const payload = await response.json();
        if (!mounted) return;

        if (payload.question) {
          setQuestion(payload.question);
        }
        setAttempts(Array.isArray(payload.attempts) ? payload.attempts : []);
      } catch (err) {
        if (mounted) setError(err.message || "Failed to load attempts");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadAttempts();
    return () => {
      mounted = false;
    };
  }, [apiBase, token, questionId, statusFilter, sortBy]);

  const attemptsCount = useMemo(() => attempts.length, [attempts]);

  const chaptersList = useMemo(() => {
    if (!question) return [];
    if (Array.isArray(question.chapters) && question.chapters.length) return question.chapters;
    if (question.chapter) return [question.chapter];
    return [];
  }, [question]);

  return (
    <section className="page">
      <div className="chapter-home-header">
        <div>
          <div className="breadcrumb">
            <Link to="/my-questions" style={{ color: "inherit", textDecoration: "none" }}>
              My Questions
            </Link>{" "}
            / {question?.title || "Custom Problem"}
          </div>
          <h2 className="page-title">{question?.title || "Problem Attempts"}</h2>
          <div style={{ display: "flex", gap: "6px", alignItems: "center", marginTop: "8px", flexWrap: "wrap" }}>
            {getDifficultyBadge(question?.difficulty)}
            {question?.grade && (
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: "#1e293b",
                  backgroundColor: "#f1f5f9",
                  padding: "2px 8px",
                  borderRadius: "6px"
                }}
              >
                {question.grade}
              </span>
            )}
            {chaptersList.map((ch) => (
              <span
                key={ch}
                style={{
                  fontSize: "11px",
                  color: "#1d4ed8",
                  backgroundColor: "#eff6ff",
                  border: "1px solid #dbeafe",
                  padding: "2px 6px",
                  borderRadius: "6px"
                }}
              >
                {ch}
              </span>
            ))}
          </div>
        </div>
        <div className="frame-detail-actions">
          <Link to="/my-questions" className="secondary-button">
            ← Back to My Questions
          </Link>
          <Link to={`/practice/custom/${questionId}`} className="primary-button">
            Solve Again
          </Link>
        </div>
      </div>

      <div className="filters-panel">
        <div className="filter-group">
          <div className="filter-label">Status</div>
          <div className="filter-row">
            {statusOptions.map((item) => (
              <button
                key={item}
                type="button"
                className={`filter-pill ${statusFilter === item ? "active" : ""}`}
                onClick={() => setStatusFilter(item)}
              >
                {item === "all" ? "All" : getStatusLabel(item)}
              </button>
            ))}
          </div>
        </div>
        <div className="filter-group align-end">
          <div className="filter-label">Sort</div>
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="question-block">
        <div className="question-title-row">
          <div className="question-meta">Problem Math & Attempt History</div>
          <div className="question-meta">{attemptsCount} attempts</div>
        </div>

        {/* Question LaTeX representation */}
        {question?.latex && (
          <div
            style={{
              margin: "12px 0 20px 0",
              padding: "16px 20px",
              backgroundColor: "#f8fafc",
              borderRadius: "10px",
              border: "1px solid #e2e8f0"
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: "700",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: "#64748b",
                marginBottom: "6px"
              }}
            >
              Problem Formulation
            </div>
            <div style={{ fontSize: "16px", overflowX: "auto" }}>
              <BlockMath math={formatLatexForDisplay(question.latex)} />
            </div>
          </div>
        )}

        {loading && <div className="question-meta">Loading attempts...</div>}
        {error && <div className="error-text">{error}</div>}
        {!loading && !error && attempts.length === 0 && (
          <div className="question-meta" style={{ padding: "30px 0", textAlign: "center" }}>
            No attempts found for this problem with the selected filter.
          </div>
        )}

        <div className="attempt-grid">
          {attempts.map((attempt) => (
            <Link
              key={attempt.id}
              to={`/attempts/${attempt.id}`}
              state={{ backTo: `${location.pathname}${location.search || ""}` }}
              className="attempt-card"
            >
              <div className="attempt-card-header">
                <div className="attempt-title">{attempt.frameTitle || question?.title || "Custom Problem"}</div>
                <span className={`attempt-badge ${getStatusClass(attempt.status)}`}>
                  {getStatusLabel(attempt.status)}
                </span>
              </div>
              {attempt.questionLatex ? (
                <div className="attempt-latex">
                  <BlockMath math={formatLatexForDisplay(attempt.questionLatex)} />
                </div>
              ) : (
                <div className="question-meta">No question text</div>
              )}
              <div className="attempt-card-footer">
                <span>{attempt.difficulty}</span>
                <span>{formatAttemptDate(attempt.createdAt)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default CustomQuestionAttempts;

